import React from 'react';
import styled from 'styled-components';

const ButtonBase = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  font-family: var(--font-family-primary);
  font-weight: 500;
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: var(--transition-base);
  text-decoration: none;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid var(--primary-green);
    outline-offset: 2px;
  }
`;

const PrimaryButton = styled(ButtonBase)`
  background: linear-gradient(135deg, var(--primary-green), var(--primary-green-dark));
  color: white;
  padding: ${props => {
    switch (props.size) {
      case 'sm': return 'var(--space-sm) var(--space-md)';
      case 'lg': return 'var(--space-lg) var(--space-xl)';
      default: return 'var(--space-md) var(--space-lg)';
    }
  }};
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1.125rem';
      default: return '1rem';
    }
  }};
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--primary-green-dark), var(--primary-green));
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled(ButtonBase)`
  background: white;
  color: var(--primary-green);
  border: 2px solid var(--primary-green);
  padding: ${props => {
    switch (props.size) {
      case 'sm': return 'calc(var(--space-sm) - 2px) calc(var(--space-md) - 2px)';
      case 'lg': return 'calc(var(--space-lg) - 2px) calc(var(--space-xl) - 2px)';
      default: return 'calc(var(--space-md) - 2px) calc(var(--space-lg) - 2px)';
    }
  }};
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1.125rem';
      default: return '1rem';
    }
  }};
  
  &:hover:not(:disabled) {
    background: var(--primary-green);
    color: white;
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const OutlineButton = styled(ButtonBase)`
  background: transparent;
  color: var(--neutral-gray-700);
  border: 1px solid var(--neutral-gray-300);
  padding: ${props => {
    switch (props.size) {
      case 'sm': return 'calc(var(--space-sm) - 1px) calc(var(--space-md) - 1px)';
      case 'lg': return 'calc(var(--space-lg) - 1px) calc(var(--space-xl) - 1px)';
      default: return 'calc(var(--space-md) - 1px) calc(var(--space-lg) - 1px)';
    }
  }};
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1.125rem';
      default: return '1rem';
    }
  }};
  
  &:hover:not(:disabled) {
    background: var(--neutral-gray-50);
    border-color: var(--neutral-gray-400);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const GhostButton = styled(ButtonBase)`
  background: transparent;
  color: var(--neutral-gray-700);
  border: none;
  padding: ${props => {
    switch (props.size) {
      case 'sm': return 'var(--space-sm) var(--space-md)';
      case 'lg': return 'var(--space-lg) var(--space-xl)';
      default: return 'var(--space-md) var(--space-lg)';
    }
  }};
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1.125rem';
      default: return '1rem';
    }
  }};
  
  &:hover:not(:disabled) {
    background: var(--neutral-gray-100);
    color: var(--primary-green);
  }
`;

const FullWidthButton = styled.div`
  width: 100%;
  
  ${ButtonBase} {
    width: 100%;
  }
`;

const Button = ({ 
  variant = 'primary', 
  size = 'md', 
  fullWidth = false, 
  children, 
  ...props 
}) => {
  const getButtonComponent = () => {
    switch (variant) {
      case 'secondary':
        return SecondaryButton;
      case 'outline':
        return OutlineButton;
      case 'ghost':
        return GhostButton;
      default:
        return PrimaryButton;
    }
  };

  const ButtonComponent = getButtonComponent();

  const button = (
    <ButtonComponent size={size} {...props}>
      {children}
    </ButtonComponent>
  );

  return fullWidth ? <FullWidthButton>{button}</FullWidthButton> : button;
};

export default Button;
